/**
 * Small rectangular house. Everything about one stamp type in one file: its dimensions, its LOD-0
 * model, and what it degrades to at each coarser level. See treeOak.ts for the shape of this file.
 *
 * THE LADDER (a fine voxel is 0.25 m; a lod-n voxel is 2^n fine). A house is 6-12 m wide and 7-11 m
 * tall — 2-4x a tree in every dimension — so it keeps a BODY two levels longer than a tree does:
 *   lod 0   0.25 m  0-40 m      walls, door, windows, floor, carved interior, voxel by voxel
 *   lod 1   0.5 m   40-80 m     plinth + solid body + gable roof (12-24 voxels wide, 7-13 roof rows)
 *   lod 2   1 m     80-160 m    the same three parts (6-12 wide, 4-7 roof rows)
 *   lod 3   2 m     160-320 m   one block plus a roof-coloured cap: under three roof rows a gable is
 *                               thin slivers, so the taper goes and the mass stays
 *   lod 4-5 4-8 m   320m-1.3km  one blob at the ridge height, plus one voxel of light if it is lit
 *   lod 5   8 m     640m-1.3km  one column, roof material: the whole house is under two voxels
 *   lod 6+                      NOTHING. A lod-6 voxel (16 m) is wider than the house, and buildings
 *                               are far too sparse to feed the per-column dot (see the registry).
 *
 * The coarse body is SOLID. The LOD-0 model carves its interior, but a carve's outcome depends on
 * apply order across a coarse chunk, and a shell one coarse voxel thick meshes as noise — so a
 * coarse building is its massing, and its windows and door are detail that ends at lod 0.
 */
import { mat } from '../../../materials/index.js';
import { sdfBoxS } from '../../../voxel/shapes.js';
import { blockHalf, buildingSink, roofCapsWall, } from '../coarseModel.js';
import { boxPart, gableParts, } from '../../../voxel/buildParts.js';
import { StampType } from '../StampTypes.js';
import { sphere } from '../../../voxel/blobs.js';
import { makeRng } from '../../../util/rng.js';
import { MAX_FOLD_LOD, MAX_SCATTER_LOD } from '../lodLevels.js';
import { SDF_THRESHOLD, pushLamp, pushTorchAt, rotateInv } from '../buildingCommon.js';
function params(variant, seed) {
    // Variation parameters
    const widthBase = 24 + (variant % 3) * 12; // 24-48 voxels (6-12m)
    const depthBase = Math.max(widthBase, 24 + (variant % 2) * 12);
    const heightBase = 14 + (variant % 3) * 2; // 14-18 voxels (3.5-4.5m)
    // Material selection
    const wallMaterials = [mat('brick2'), mat('brick7'), mat('brick3')];
    const roofMaterials = [mat('roof'), mat('roof2')];
    const floorMaterials = [mat('brick8'), mat('tile3')];
    const wallMaterial = wallMaterials[variant % wallMaterials.length];
    const roofMaterial = roofMaterials[variant % roofMaterials.length];
    const floorMaterial = floorMaterials[variant % floorMaterials.length];
    const foundationMaterial = mat('cobble');
    const halfWidth = widthBase / 2;
    const halfDepth = depthBase / 2;
    const wallThickness = 2; // 2 voxels for proper rotation support
    // Roof parameters
    const roofPeakHeight = Math.floor(widthBase / 2) + 2;
    const roofOverhang = 2;
    // Door and window parameters
    const doorHalfWidth = 3;
    const doorHeight = 8;
    const windowY = 5;
    const windowHalfSize = 3;
    const windowHeight = 4;
    // Calculate bounding box with rotation margin
    const maxDim = Math.ceil(Math.max(halfWidth, halfDepth) + roofOverhang + 5);
    const maxHeight = heightBase + roofPeakHeight + 2;
    const rng = makeRng(seed);
    const lavaRoll = rng();
    const torchRoll = rng();
    return {
        widthBase, depthBase, heightBase,
        wallMaterial, roofMaterial, floorMaterial, foundationMaterial,
        halfWidth, halfDepth, wallThickness,
        roofPeakHeight, roofOverhang,
        doorHalfWidth, doorHeight, windowY, windowHalfSize, windowHeight,
        maxDim, maxHeight,
        lavaRoll, torchRoll,
    };
}
/** LOD 0 — the original generateSmallBuildingSDF body, reading its numbers from params. */
function lod0(p, _variant, seed, rotation) {
    const voxels = [];
    const { heightBase, wallMaterial, roofMaterial, floorMaterial, foundationMaterial, halfWidth, halfDepth, wallThickness, roofPeakHeight, roofOverhang, doorHalfWidth, doorHeight, windowY, windowHalfSize, windowHeight, maxDim, maxHeight, } = p;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    // Sample SDF over the entire bounding volume
    for (let y = -4; y <= maxHeight; y++) {
        for (let x = -maxDim; x <= maxDim; x++) {
            for (let z = -maxDim; z <= maxDim; z++) {
                // Rotate query point to local building space (inverse rotation)
                const { x: rx, z: rz } = rotateInv(x, z, cos, sin);
                // Evaluate what's at this rotated position using proper SDFs
                const hit = evaluateSmallBuildingSDF(rx, y, rz, halfWidth, halfDepth, heightBase, wallThickness, roofPeakHeight, roofOverhang, doorHalfWidth, doorHeight, windowY, windowHalfSize, windowHeight, wallMaterial, foundationMaterial, floorMaterial, roofMaterial);
                if (hit) {
                    voxels.push({
                        x, y, z,
                        material: SAMPLE.material,
                        weight: SAMPLE.weight,
                    });
                }
            }
        }
    }
    // --- Decorations (deterministic per building) ---
    // The rolls come from params, in the order LOD 0 draws them: `params` owns the makeRng(seed)
    // stream because a DIMENSION can come out of it (the tower's floor count does), and a second
    // makeRng(seed) here would hand this decoration the draw that belongs to the first one.
    // Ceiling lava blob on 50% of buildings, at the ceiling centre (rotation-invariant).
    if (p.lavaRoll < 0.5) {
        for (const v of sphere(0, heightBase - 2, 0, 2.5, mat('lava'), 0.4, seed))
            voxels.push(v);
    }
    // Exterior torches above the door on 25% of buildings (front wall = -Z, door centred x=0).
    if (p.torchRoll < 0.25) {
        const zOuter = -halfDepth;
        pushTorchAt(voxels, -(doorHalfWidth + 1), zOuter, doorHeight, cos, sin);
        pushTorchAt(voxels, doorHalfWidth + 1, zOuter, doorHeight, cos, sin);
    }
    return voxels;
}
/** Top of the LOD-0 silhouette: the roof ridge. Seed-independent (the size is variant-derived). */
function topFine(p) {
    return p.heightBase + p.roofPeakHeight;
}
function lodParts(p, lod) {
    const stride = 1 << lod;
    const top = topFine(p);
    const sink = buildingSink(Math.max(p.halfWidth, p.halfDepth), stride);
    // THE BODY RUNS ONE STRIDE PAST THE EAVE, up inside the roof — parts that merely touch both read
    // exactly zero on the shared plane and NEITHER emits, which would leave the eave row empty. Sinking
    // the ROOF instead is what this did first, and it costs the roof its width where the width matters:
    // a gable whose stated base sits below the eave has already narrowed by the time it reaches it.
    const bodyTop = p.heightBase + stride;
    if (lod <= 2) {
        // The plinth is LOD 0's foundation: one voxel proud of the walls, and the depth that keeps the
        // downhill corner of a 12 m box out of the air (buildingSink). The gable is held one lattice point
        // clear of the body while the level can still show an overhang (roofCapsWall) — LOD 0's own
        // overhang is 2 fine voxels, which is sub-voxel from lod 2 out, and a roof flush with its walls
        // stops reading as a roof.
        // Gable FIRST, so it owns the rows it shares with the body — those rows are the eave.
        const parts = [
            ...gableParts(roofCapsWall(p.halfWidth + p.roofOverhang, p.halfWidth, stride), roofCapsWall(p.halfDepth + p.roofOverhang, p.halfDepth, stride), p.heightBase, top, p.roofMaterial, stride),
            boxPart(p.halfWidth + 1, p.halfDepth + 1, -sink, 1, p.foundationMaterial),
            boxPart(p.halfWidth, p.halfDepth, 0, bodyTop, p.wallMaterial),
        ];
        pushLamp(parts, hasLamp(p), p.doorHeight, p.halfDepth, lod, stride);
        return parts;
    }
    if (lod === 3) {
        // NO GABLE BELOW THREE ROOF ROWS, and no plinth either — the same rule the pine's crown follows.
        // At 2 m the roof is 2-3 rows and the prism's upper rows are thin slivers: measured at lod 3 they
        // packed to 0.17 and 0.10 against a solid 0.50, so the ridge read as a soft smear rather than a
        // roof. A narrowed CAP keeps the roof's mass and its colour, which is what carries at this range —
        // a village of grey blocks reads as rock. Half-extents go through `blockHalf`, which breaks the
        // lattice tie upward (a 12 m house at lod 3 drew 10 m where 14 m was equally close) and holds a
        // sub-voxel box to one solid voxel (a 6 m house is 0.75 of a lod-4 voxel wide).
        const parts = [
            boxPart(blockHalf(p.halfWidth * 0.8, stride), blockHalf(p.halfDepth * 0.8, stride), p.heightBase, top, p.roofMaterial),
            boxPart(blockHalf(p.halfWidth, stride), blockHalf(p.halfDepth, stride), -sink, bodyTop, p.wallMaterial),
        ];
        pushLamp(parts, hasLamp(p), p.doorHeight, p.halfDepth, lod, stride);
        return parts;
    }
    // lod 4-5: ONE BLOB, plus one voxel of light beside it if this building is a lit one — the trees'
    // answer at these strides, with one difference that matters. A tree is genuinely narrower than a
    // lod-4 voxel, so its blob is a single column; a 12 m house is three voxels wide, and drawing it as
    // one would have it swell to three times its size as you cross into lod 3. `blockHalf` sizes the blob
    // to the building and converges on a single voxel by lod 5 on its own.
    const blob = [boxPart(blockHalf(p.halfWidth, stride), blockHalf(p.halfDepth, stride), -sink, top, p.roofMaterial)];
    pushLamp(blob, hasLamp(p), p.doorHeight, p.halfDepth, lod, stride);
    return blob;
}
/**
 * Whether this instance carries exterior torches — LOD 0's own roll, read in one place because the
 * coarse cache keys on it (see coarseSeedClass). The FRACTION is this building's own decision; what
 * a lamp then IS at a coarse level is shared (buildingCommon.pushLamp).
 */
const LAMP_CHANCE = 0.25;
function hasLamp(p) {
    return p.torchRoll < LAMP_CHANCE;
}
export const BUILDING_SMALL_SPEC = {
    type: StampType.BUILDING_SMALL,
    maxLod: MAX_SCATTER_LOD,
    foldLod: MAX_FOLD_LOD,
    params,
    lod0,
    lodParts,
    // The lamp is the one seed-derived fact the coarse model reads, so it is the whole shape class:
    // without this the cache would serve a lit building's model to an unlit one and vice versa (caught
    // by lodStandIn's seed-class case).
    coarseSeedClass: (seed) => (hasLamp(params(0, seed)) ? 1 : 0),
};
/**
 * The evaluator's result, as a scratch rather than a return object: it is read ~95k times per placed
 * building (the sample loop in lod0), and a `{ material, weight }` per hit was the bulk of that cost.
 * Written on `true`, undefined on `false`.
 */
const SAMPLE = { material: 0, weight: 0 };
/**
 * Evaluate small building using proper SDF at a point in local (unrotated) space
 */
function evaluateSmallBuildingSDF(x, y, z, halfWidth, halfDepth, heightBase, wallThickness, roofPeakHeight, roofOverhang, doorHalfWidth, doorHeight, windowY, windowHalfSize, windowHeight, wallMaterial, foundationMaterial, floorMaterial, roofMaterial) {
    // Foundation SDF (-4 to 0, extends slightly beyond walls)
    const foundationHalfHeight = 2;
    const foundationY = -2; // Center at y=-2
    const foundationDist = sdfBoxS(x, y - foundationY, z, halfWidth + 1, foundationHalfHeight, halfDepth + 1);
    if (foundationDist < SDF_THRESHOLD && y >= -4 && y <= 0) {
        {
            SAMPLE.material = foundationMaterial;
            SAMPLE.weight = 0.45;
            return true;
        }
    }
    // Stairs in front of door
    if (y >= -3 && y <= 0) {
        const stairStep = -y;
        for (let step = 0; step < 4; step++) {
            if (stairStep === step) {
                const stairZ = -halfDepth - 1.5 - step;
                const stairDist = sdfBoxS(x, 0, z - stairZ, doorHalfWidth, 0.5, 0.5);
                if (stairDist < SDF_THRESHOLD) {
                    {
                        SAMPLE.material = foundationMaterial;
                        SAMPLE.weight = 0.45;
                        return true;
                    }
                }
            }
        }
    }
    // Doorway carving - extends through wall and slightly in front to clear terrain
    if (y >= 0 && y < doorHeight) {
        const doorwayDist = sdfBoxS(x, y - doorHeight / 2, z + halfDepth + 1, doorHalfWidth + 0.5, doorHeight / 2, wallThickness + 2);
        if (doorwayDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = 0;
                SAMPLE.weight = -0.5;
                return true;
            } // Carve out doorway
        }
    }
    // Floor SDF (y = 0, inside walls)
    if (y >= 0 && y < 1) {
        const floorDist = sdfBoxS(x, y - 0.5, z, halfWidth - 0.5, 0.5, halfDepth - 0.5);
        if (floorDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = floorMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Interior air (carve out inside) - must check before walls
    if (y >= 1 && y < heightBase) {
        const interiorDist = sdfBoxS(x, y - heightBase / 2, z, halfWidth - wallThickness, heightBase / 2, halfDepth - wallThickness);
        if (interiorDist < -SDF_THRESHOLD) { // Well inside interior
            {
                SAMPLE.material = 0;
                SAMPLE.weight = -0.5;
                return true;
            }
        }
    }
    // Ceiling
    if (y >= heightBase - 1 && y <= heightBase) {
        const ceilingDist = sdfBoxS(x, y - heightBase + 0.5, z, halfWidth - 0.5, 0.5, halfDepth - 0.5);
        if (ceilingDist < SDF_THRESHOLD) {
            {
                SAMPLE.material = mat('concrete');
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Walls using hollow box SDF
    if (y >= 1 && y <= heightBase) {
        // Outer box
        const outerDist = sdfBoxS(x, y - (heightBase + 1) / 2, z, halfWidth, heightBase / 2, halfDepth);
        // Inner box (hollow)
        const innerDist = sdfBoxS(x, y - (heightBase + 1) / 2, z, halfWidth - wallThickness, heightBase / 2 + 1, halfDepth - wallThickness);
        // Wall shell: inside outer, outside inner
        if (outerDist < SDF_THRESHOLD && innerDist > -SDF_THRESHOLD) {
            // Check for door (front wall, negative Z)
            if (z < -halfDepth + wallThickness + SDF_THRESHOLD && y <= doorHeight) {
                const doorDist = sdfBoxS(x, y - doorHeight / 2, z + halfDepth, doorHalfWidth, doorHeight / 2, wallThickness + 1);
                if (doorDist < SDF_THRESHOLD) {
                    return false; // Door opening
                }
            }
            // Check for windows - carve terrain like interior
            const inWindowY = y >= windowY && y < windowY + windowHeight;
            if (inWindowY) {
                // Side windows (on X walls) - spaced further apart
                if (Math.abs(x) > halfWidth - wallThickness - SDF_THRESHOLD) {
                    const numSideWindows = Math.max(1, Math.floor(halfDepth / 10));
                    for (let w = 0; w < numSideWindows; w++) {
                        const windowZ = -halfDepth / 2 + (w + 0.5) * (halfDepth / numSideWindows);
                        const windowDist = sdfBoxS(0, y - windowY - windowHeight / 2, z - windowZ, wallThickness + 1, windowHeight / 2, windowHalfSize);
                        if (windowDist < SDF_THRESHOLD) {
                            {
                                SAMPLE.material = 0;
                                SAMPLE.weight = -0.5;
                                return true;
                            } // Carve window opening
                        }
                    }
                }
                // Back windows (on positive Z wall) - spaced further apart
                if (z > halfDepth - wallThickness - SDF_THRESHOLD) {
                    const numBackWindows = Math.max(1, Math.floor(halfWidth / 10));
                    for (let w = 0; w < numBackWindows; w++) {
                        const windowX = -halfWidth / 2 + (w + 0.5) * (halfWidth / numBackWindows);
                        const windowDist = sdfBoxS(x - windowX, y - windowY - windowHeight / 2, 0, windowHalfSize, windowHeight / 2, wallThickness + 1);
                        if (windowDist < SDF_THRESHOLD) {
                            {
                                SAMPLE.material = 0;
                                SAMPLE.weight = -0.5;
                                return true;
                            } // Carve window opening
                        }
                    }
                }
            }
            {
                SAMPLE.material = wallMaterial;
                SAMPLE.weight = 0.45;
                return true;
            }
        }
    }
    // Pitched roof
    if (y > heightBase && y <= heightBase + roofPeakHeight) {
        const roofY = y - heightBase - 1;
        const halfWidthAtY = halfWidth + roofOverhang - roofY * 0.8;
        if (halfWidthAtY >= 0) {
            const roofDist = sdfBoxS(x, 0, z, halfWidthAtY, 0.5, halfDepth + roofOverhang);
            if (roofDist < SDF_THRESHOLD) {
                {
                    SAMPLE.material = roofMaterial;
                    SAMPLE.weight = 0.45;
                    return true;
                }
            }
        }
    }
    return false;
}
//# sourceMappingURL=buildingSmall.js.map